import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";

import { PurClientConfig } from "react-lib/apis/PUR/purApi";
import { PurDocumentVerificationAccess } from "react-lib/apis/PUR/purDocumentVerification";
import {
  PUR_DOCUMENT_TYPES,
  PurDocument,
  PurDocumentLine,
  PurDocumentLinePage,
  PurDocumentPage,
  PurDocumentType,
} from "react-lib/apis/PUR/purDocumentsApi";

import { Event as MeERPEvent, MeERPSlice, nextMeERPRequestKey } from "./MeERPInterface";
import { formatPurTimestamp } from "./purPresentation";
import PurDocumentVerification from "./PurDocumentVerification";
import { PurVerificationStore, usePurVerificationStore } from "./purVerificationState";

const PAGE_SIZE = 20;
interface ClientProps { client: PurClientConfig }
interface EventProps { onEvent: (event: MeERPEvent) => void; meerp: MeERPSlice }
interface WorkspaceProps extends ClientProps, EventProps { verificationAccess?: PurDocumentVerificationAccess; verificationStore?: PurVerificationStore }
interface DetailProps extends WorkspaceProps { id: number; onClose: () => void; onChanged: () => void }
interface LinesProps extends ClientProps, EventProps { document: PurDocument }

const useLabels = () => {
  const intl = useIntl();
  return {
    intl,
    value: (value: string | number | null) => value === null || value === "" ? intl.formatMessage({ id: "MEERP_UNKNOWN" }) : String(value),
    verified: (value: boolean | null) => intl.formatMessage({ id: value === null ? "MEERP_UNKNOWN" : value ? "MEERP_DOCUMENT_VERIFIED" : "MEERP_DOCUMENT_UNVERIFIED" }),
    type: (value: string) => PUR_DOCUMENT_TYPES.some((type) => type === value) ? intl.formatMessage({ id: `MEERP_DOC_TYPE_${value}` }) : value || intl.formatMessage({ id: "MEERP_UNKNOWN" }),
  };
};

interface PaginationProps { offset: number; count: number; disabled?: boolean; onChange: (offset: number) => void }
const Pagination: React.FC<PaginationProps> = ({ offset, count, disabled, onChange }) => {
  const { intl } = useLabels();
  return <div className="card-meerp__pagination">
    <button className="card-meerp__button" type="button" disabled={disabled || offset === 0} onClick={() => onChange(Math.max(0, offset - PAGE_SIZE))}>{intl.formatMessage({ id: "MEERP_PREVIOUS" })}</button>
    <span>{intl.formatMessage({ id: "MEERP_PAGE" }, { current: Math.floor(offset / PAGE_SIZE) + 1, total: Math.max(1, Math.ceil(count / PAGE_SIZE)) })}</span>
    <button className="card-meerp__button" type="button" disabled={disabled || offset + PAGE_SIZE >= count} onClick={() => onChange(offset + PAGE_SIZE)}>{intl.formatMessage({ id: "MEERP_NEXT" })}</button>
  </div>;
};

const DocumentLineTable: React.FC<{ lines: PurDocumentLine[] }> = ({ lines }) => {
  const { intl, value } = useLabels();
  const headers = ["MEERP_PRODUCT", "MEERP_PRODUCT_ID", "MEERP_PO_LINE_ID", "MEERP_RECEIPT_LINE_ID", "MEERP_INVOICED_QUANTITY", "MEERP_INVOICE_UNIT_COST", "MEERP_TAXABLE_AMOUNT", "MEERP_FIELD_VAT", "MEERP_TOTAL", "MEERP_PRICE_VARIANCE", "MEERP_NOTES"];
  return lines.length === 0 ? <p>{intl.formatMessage({ id: "MEERP_NO_ITEMS" })}</p> : <div className="card-meerp__table-wrap"><table className="card-meerp__table">
    <caption className="card-meerp__sr-only">{intl.formatMessage({ id: "MEERP_DOCUMENT_LINES" })}</caption>
    <thead><tr>{headers.map((header) => <th scope="col" key={header}>{intl.formatMessage({ id: header })}</th>)}</tr></thead>
    <tbody>{lines.map((line, index) => <tr key={line.id ?? index}>
      {[line.productName, line.productId, line.poItemId, line.receiptItemId, line.invoicedQuantity, line.unitCost, line.taxableAmount, line.vatAmount, line.lineTotal, line.priceVariance, line.notes].map((cell, column) => <td key={column}>{value(cell)}</td>)}
    </tr>)}</tbody>
  </table></div>;
};

const DocumentLines: React.FC<LinesProps> = ({ onEvent, meerp, document }) => {
  const { intl } = useLabels();

  const [paged, setPaged] = useState(document.items === null);
  const [offset, setOffset] = useState(0);
  const [refresh, setRefresh] = useState(0);
  const [requestKey, setRequestKey] = useState(() => nextMeERPRequestKey());

  useEffect(() => {
    if (!paged) return;
    const key = nextMeERPRequestKey();
    setRequestKey(key);
    onEvent({ message: "GetPurDocumentLines", params: { id: document.id, limit: PAGE_SIZE, offset, requestKey: key } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document.id, offset, paged, refresh]);

  const linesState = meerp.documentLines[String(document.id)];
  const linesCurrent = linesState?.requestKey === requestKey;
  const page: PurDocumentLinePage | null = linesCurrent ? linesState.page : null;
  const loading = linesCurrent ? linesState.loading : true;
  const error = linesCurrent ? linesState.errorId : null;

  return <section aria-label={intl.formatMessage({ id: "MEERP_DOCUMENT_LINES" })}>
    <h4>{intl.formatMessage({ id: "MEERP_DOCUMENT_LINES" })}</h4>
    <p className="card-meerp__muted">{intl.formatMessage({ id: "MEERP_INVOICE_UNITS_NOTE" })}</p>
    {!paged && document.items !== null && <>
      <DocumentLineTable lines={document.items} />
      <button type="button" className="card-meerp__button card-meerp__secondary" onClick={() => setPaged(true)}>{intl.formatMessage({ id: "MEERP_LOAD_PAGED_LINES" })}</button>
    </>}
    {paged && <>
      {loading && <p role="status">{intl.formatMessage({ id: "MEERP_LOADING" })}</p>}
      {error && <div role="alert"><p>{intl.formatMessage({ id: error })}</p><button className="card-meerp__button" type="button" onClick={() => setRefresh((key) => key + 1)}>{intl.formatMessage({ id: "MEERP_RETRY" })}</button></div>}
      {page && <><DocumentLineTable lines={page.records} /><Pagination offset={offset} count={page.count} onChange={setOffset} disabled={loading} /></>}
    </>}
  </section>;
};

const DocumentDetail: React.FC<DetailProps> = ({ onEvent, meerp, client, id, onClose, verificationAccess, verificationStore, onChanged }) => {
  const { intl, value, verified, type } = useLabels();
  const [refresh, setRefresh] = useState(0);
  const [requestKey, setRequestKey] = useState(() => nextMeERPRequestKey());
  // Documents handed back by the verification write flow after a mutation.
  const [documentOverride, setDocumentOverride] = useState<PurDocument | null>(null);
  useEffect(() => {
    setDocumentOverride(null);
    const key = nextMeERPRequestKey();
    setRequestKey(key);
    onEvent({ message: "GetPurDocumentDetail", params: { id, requestKey: key } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, refresh]);

  const detailState = meerp.documentDetails[String(id)];
  const detailCurrent = detailState?.requestKey === requestKey;
  const document: PurDocument | null = documentOverride ?? (detailCurrent ? detailState.document : null) ?? null;
  const loading = documentOverride === null && (detailCurrent ? detailState.loading : true);
  const error = detailCurrent ? detailState.errorId : null;

  return <section className="card-meerp__detail" aria-label={intl.formatMessage({ id: "MEERP_DOCUMENT_DETAIL" })}>
    <div className="card-meerp__detail-header"><h3>{intl.formatMessage({ id: "MEERP_DOCUMENT_DETAIL" })}{document ? ` · ${document.number}` : ""}</h3><button className="card-meerp__button" type="button" onClick={onClose}>{intl.formatMessage({ id: "MEERP_CLOSE_DETAIL" })}</button></div>
    {loading && <p role="status">{intl.formatMessage({ id: "MEERP_LOADING" })}</p>}
    {error && <div role="alert"><p>{intl.formatMessage({ id: error })}</p><button className="card-meerp__button" type="button" onClick={() => setRefresh((key) => key + 1)}>{intl.formatMessage({ id: "MEERP_RETRY" })}</button></div>}
    {document && <>
      <dl className="card-meerp__facts">{[
        ["MEERP_FIELD_DOCUMENT_TYPE", type(document.type)], ["MEERP_FIELD_DOCUMENT_DATE", document.date], ["MEERP_VENDOR", document.vendorName],
        ["MEERP_CREATED_AT", formatPurTimestamp(intl, document.createdAt)], ["MEERP_EDITED_AT", formatPurTimestamp(intl, document.editedAt)],
        ["MEERP_PROJECT", document.projectNumber], ["MEERP_PO_ID", document.poId], ["MEERP_RECEIPT_ID", document.receiptId],
        ["MEERP_DOCUMENT_VERIFICATION", verified(document.verified)], ["MEERP_TOTAL", document.grossAmount],
        ["MEERP_TAXABLE_AMOUNT", document.taxableAmount], ["MEERP_FIELD_VAT", document.vatAmount],
        ["MEERP_EXEMPT_AMOUNT", document.exemptAmount], ["MEERP_FIELD_WITHHOLDING", document.withholdingAmount],
        ["MEERP_PRICE_VARIANCE", document.totalPriceVariance],
        ["MEERP_HAS_PRICE_VARIANCE", document.hasPriceVariance === null ? null : intl.formatMessage({ id: document.hasPriceVariance ? "MEERP_OPTION_YES" : "MEERP_OPTION_NO" })],
      ].map(([label, content]) => <div key={label}><dt>{intl.formatMessage({ id: String(label) })}</dt><dd>{value(content)}</dd></div>)}</dl>
      <p>{document.attachmentUrl ? <a href={document.attachmentUrl} target="_blank" rel="noopener noreferrer">{intl.formatMessage({ id: "MEERP_OPEN_ATTACHMENT" })}</a> : intl.formatMessage({ id: "MEERP_NO_ATTACHMENT" })}</p>
      <p className="card-meerp__muted">{intl.formatMessage({ id: "MEERP_ATTACHMENT_NOT_PRINT" })}</p>
      <DocumentLines key={`${document.id}:${refresh}`} onEvent={onEvent} meerp={meerp} client={client} document={document} />
      <PurDocumentVerification client={client} documentId={id} access={verificationAccess} store={verificationStore} onVerified={(verifiedDocument) => { setDocumentOverride(verifiedDocument); onChanged(); }} />
    </>}
  </section>;
};

const PurDocuments: React.FC<WorkspaceProps> = ({ onEvent, meerp, client, verificationAccess, verificationStore }) => {
  const localStore = usePurVerificationStore();
  const { intl, value, verified, type } = useLabels();
  const [docType, setDocType] = useState<PurDocumentType | "">("");
  const [verification, setVerification] = useState("");
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<number | null>(null);
  const [refresh, setRefresh] = useState(0);
  const [requestKey, setRequestKey] = useState(() => nextMeERPRequestKey());

  useEffect(() => {
    const key = nextMeERPRequestKey();
    setRequestKey(key);
    onEvent({
      message: "GetPurDocuments",
      params: {
        limit: PAGE_SIZE,
        offset,
        type: docType || undefined,
        verified: verification === "" ? undefined : verification === "true",
        requestKey: key,
      },
    });
    // The parent remounts this workspace when the division/client context changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docType, verification, offset, refresh]);

  const documentsState = meerp.documents;
  const documentsCurrent = documentsState.requestKey === requestKey;
  const page: PurDocumentPage | null = documentsCurrent ? documentsState.page : null;
  const loading = documentsCurrent ? documentsState.loading : true;
  const error = documentsCurrent ? documentsState.errorId : null;

  const resetSelection = () => { setOffset(0); setSelected(null); };
  return <div className="card-meerp__workspace">
    <p className="card-meerp__muted">{intl.formatMessage({ id: "MEERP_DOCUMENT_VERIFY_MODE" })} · {intl.formatMessage({ id: "MEERP_BILLING_NOT_PAYMENT" })}</p>
    <div className="card-meerp__toolbar">
      <label htmlFor="meerp-document-type">{intl.formatMessage({ id: "MEERP_FIELD_DOCUMENT_TYPE" })}</label>
      <select id="meerp-document-type" value={docType} onChange={(event) => { setDocType(event.target.value as PurDocumentType | ""); resetSelection(); }}>
        <option value="">{intl.formatMessage({ id: "MEERP_ALL_DOCUMENT_TYPES" })}</option>
        {PUR_DOCUMENT_TYPES.map((docType) => <option key={docType} value={docType}>{type(docType)}</option>)}
      </select>
      <label htmlFor="meerp-document-verification">{intl.formatMessage({ id: "MEERP_DOCUMENT_VERIFICATION" })}</label>
      <select id="meerp-document-verification" value={verification} onChange={(event) => { setVerification(event.target.value); resetSelection(); }}>
        <option value="">{intl.formatMessage({ id: "MEERP_STATUS_ALL" })}</option><option value="false">{verified(false)}</option><option value="true">{verified(true)}</option>
      </select>
      <button type="button" className="card-meerp__button card-meerp__secondary" onClick={() => { setRefresh((key) => key + 1); setSelected(null); }}>{intl.formatMessage({ id: "MEERP_REFRESH" })}</button>
    </div>
    {loading && <p role="status">{intl.formatMessage({ id: "MEERP_LOADING" })}</p>}
    {error && <div role="alert" className="card-meerp__message--error"><p>{intl.formatMessage({ id: error })}</p><button className="card-meerp__button" type="button" onClick={() => setRefresh((key) => key + 1)}>{intl.formatMessage({ id: "MEERP_RETRY" })}</button></div>}
    {page && <>
      {page.records.length === 0 ? <p>{intl.formatMessage({ id: "MEERP_EMPTY" })}</p> : <div className="card-meerp__table-wrap"><table className="card-meerp__table">
        <caption className="card-meerp__sr-only">{intl.formatMessage({ id: "MEERP_VENDOR_DOCUMENTS" })}</caption>
        <thead><tr>{["MEERP_NUMBER", "MEERP_FIELD_DOCUMENT_TYPE", "MEERP_FIELD_DOCUMENT_DATE", "MEERP_CREATED_AT", "MEERP_VENDOR", "MEERP_TOTAL", "MEERP_DOCUMENT_VERIFICATION", "MEERP_PRICE_VARIANCE", "MEERP_DETAIL"].map((id) => <th scope="col" key={id}>{intl.formatMessage({ id: id })}</th>)}</tr></thead>
        <tbody>{page.records.map((doc) => <tr key={doc.id}>
          <td>{doc.number}</td><td>{type(doc.type)}</td><td>{value(doc.date)}</td><td>{formatPurTimestamp(intl, doc.createdAt)}</td><td>{value(doc.vendorName)}</td><td>{value(doc.grossAmount)}</td><td>{verified(doc.verified)}</td><td>{value(doc.totalPriceVariance)}</td>
          <td><button type="button" className="card-meerp__link" onClick={() => setSelected(doc.id)}>{intl.formatMessage({ id: "MEERP_OPEN_DETAIL" })}</button></td>
        </tr>)}</tbody>
      </table></div>}
      <Pagination offset={offset} count={page.count} disabled={loading} onChange={(next) => { setOffset(next); setSelected(null); }} />
    </>}
    {selected !== null && <DocumentDetail key={selected} onEvent={onEvent} meerp={meerp} client={client} id={selected} verificationAccess={verificationAccess} verificationStore={verificationStore || localStore} onClose={() => setSelected(null)} onChanged={() => setRefresh((key) => key + 1)} />}
  </div>;
};

export default PurDocuments;
